import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Star, Truck, ShieldCheck, RotateCcw, Plus, Minus, Check, Lock, Zap, Snowflake, Play, Quote, BadgeCheck, Package, ListChecks, Sparkles, Lightbulb, Award, Info } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CATALOG, fmt } from "@/data/catalog";
import { mediaUrl as M } from "@/data/pdpMedia";
import { posterUrl } from "@/data/pdpMedia";
import { VideoReel } from "@/components/product/VideoReel";
import { HIGHLIGHTS } from "@/data/product";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { Stars } from "@/components/common/ui";
import { txt } from "@/data/siteText";

const arrivalBy = () => {
  const fmtD = (d) => d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
  const a = new Date(); a.setDate(a.getDate() + 4);
  const b = new Date(); b.setDate(b.getDate() + 8);
  return `${fmtD(a)} – ${fmtD(b)}`;
};
import { PaymentIcons, AfterpayBadge } from "@/components/common/PaymentIcons";

const INCLUDED = ["Leopard Luxe™ LED Light Therapy Mask", "Smart remote control", "Adjustable head straps", "USB-C charging cable", "Silk carry bag", "User guide"];
const SPECS = ["53 mW/cm² irradiance", "114 high-performance LEDs", "4 targeted wavelengths", "6 customisable modes", "Medical-grade silicone", "USB-C rechargeable", "Built-in eye protection", "Travel-friendly design"];
const STEPS = [
  "Cleanse — wash your face and pat dry. Use on clean, bare skin so the light can work its magic.",
  "Mask up — adjust the straps, press power and pick your light mode, then relax for 10 minutes (3 to 5 times a week).",
  "Glow on — remove the mask, apply your favourite serum and moisturiser, then wipe the mask clean and store.",
];

export const BuyBox = () => {
  const VIDS = [
    { src: M("pdp_buybox_video_1"), label: "8-week results", slot: "pdp_buybox_video_1" },
    { src: M("pdp_buybox_video_2"), label: "See the glow", slot: "pdp_buybox_video_2" },
    { src: M("pdp_ugc_3"), label: "Nightly ritual", slot: "pdp_ugc_3" },
    { src: M("pdp_ugc_4"), label: "Real results", slot: "pdp_ugc_4" },
  ];
  const reelClips = VIDS.map((v) => ({ src: v.src, poster: posterUrl(v.slot), title: v.label }));
  const [qty, setQty] = useState(1);
  const [reelIndex, setReelIndex] = useState(null);
  const { addItem, setOpen } = useCart();
  const p = CATALOG.single;

  const addToBag = (openDrawer = true) => {
    addItem("single", qty);
    toast.success(`${p.name} added to your bag`, { description: "Free express shipping included." });
    if (openDrawer) setOpen(true);
  };


  return (
    <div data-testid="buy-box">
      <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-[#F2E8E6] px-3.5 py-1.5 text-[0.6rem] uppercase tracking-[0.18em] text-[#232222] font-semibold">
        <Zap className="h-3 w-3 text-[#F6B393]" /> Best Seller
      </span>
      <h1 className="font-display leading-[1.02] tracking-[-0.01em]" data-testid="product-title">
        <span className="block text-3xl font-light italic text-[#F4B999] sm:text-4xl" data-text-slot="pdp_title_brand">{txt("pdp_title_brand", "Leopard Luxe™")}</span>
        <span className="block text-4xl font-light text-[#2C2B2C] sm:text-5xl" data-text-slot="pdp_title_name">{txt("pdp_title_name", "LED Light Therapy Mask")}</span>
      </h1>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
        <Stars n={5} />
        <a href="#reviews" className="text-sm text-[#8A7F86] underline-offset-4 hover:underline">4.9 · 50+ reviews</a>
      </div>

      <div className="mt-5 flex items-baseline gap-3">
        <span className="font-display text-4xl text-[#2C2B2C]" data-testid="buybox-price">{fmt(p.price)}</span>
        {p.compareAt > p.price && (
          <>
            <span className="text-lg text-[#8A7F86] line-through">{fmt(p.compareAt)}</span>
            <span className="rounded-full bg-[#F2E8E6] px-3 py-1 text-[0.62rem] font-bold uppercase tracking-widest text-[#232222]">Save {fmt(p.compareAt - p.price)}</span>
          </>
        )}
      </div>
      <p className="mt-1.5 flex flex-wrap items-center gap-1.5 text-sm text-[#8A7F86]">Or 4 interest-free payments of {fmt(p.price / 4)} with <AfterpayBadge /></p>

      <p className="mt-5 text-[0.92rem] font-light leading-relaxed text-[#6E6469]" data-testid="buybox-description">
        Experience the world's first Leopard Print LED Light Therapy Mask. Turn back the clock with clinical-grade LED technology, wrapped in a bold, luxurious design that transforms your skincare routine into a self-care ritual.
      </p>

      {/* Highlights */}
      <ul className="mt-6 space-y-2.5">
        {HIGHLIGHTS.map(([t, d]) => (
          <li key={t} className="flex items-start gap-3">
            <Check className="mt-0.5 h-4 w-4 flex-none text-[#FBC4A8]" strokeWidth={2.5} />
            <span className="text-[0.8rem] leading-snug text-[#8A7F86] lg:whitespace-nowrap"><strong className="font-medium text-[#2C2B2C]">{t}</strong> — {d}</span>
          </li>
        ))}
      </ul>

      {/* Featured review, compact */}
      <figure className="mt-5 flex items-start gap-3 rounded-xl border border-[#F2E8E6] bg-[#FFFCFA] px-4 py-3" data-testid="buybox-review">
        <img src={M("pdp_buybox_avatar")} data-media-slot="pdp_buybox_avatar" data-media-type="image" alt="Jess M." className="mt-0.5 h-8 w-8 flex-none rounded-full object-cover" />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <Stars n={5} className="h-3 w-3" />
            <span className="text-[0.66rem] font-medium text-[#8A7F86]">Jess M.</span>
            <BadgeCheck className="h-3 w-3 text-[#3B8C6E]" />
          </div>
          <p className="mt-1 text-[0.8rem] italic leading-snug text-[#5F555B]">“Three weeks in and my skin is genuinely brighter, my breakouts have calmed right down.”</p>
          <button onClick={() => document.querySelector('[data-testid="testimonials-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" })}
            data-testid="buybox-see-reviews"
            className="mt-1 inline-flex items-center gap-1 text-[0.68rem] font-semibold text-[#E97C6A] underline-offset-2 hover:underline">
            See more reviews →
          </button>
        </div>
      </figure>

      {/* Qty + CTA */}
      <div className="mt-6 flex items-stretch gap-3">
        <div className="flex items-center rounded-full border border-[#2C2B2C]">
          <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="product-qty-dec" className="px-4"><Minus className="h-4 w-4" /></button>
          <span className="w-10 text-center" data-testid="product-qty">{qty}</span>
          <button onClick={() => setQty(qty + 1)} data-testid="product-qty-inc" className="px-4"><Plus className="h-4 w-4" /></button>
        </div>
        <button onClick={() => addToBag(true)} data-testid="product-add-to-cart"
          className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#FBC4A8] px-6 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#232222] shadow-[0_12px_30px_-12px_rgba(202,166,140,0.65)] transition-all hover:bg-[#F6B393] hover:tracking-[0.22em]">
          Add to Bag
        </button>
      </div>

      {/* Trust pills */}
      <div className="mt-4 grid grid-cols-3 gap-2">
        {[[Truck, "Free", "express delivery"], [RotateCcw, "30-day", "guarantee"], [ShieldCheck, "12-month", "warranty"]].map(([Icon, lead, rest]) => (
          <div key={rest} className="flex items-center justify-center gap-2 rounded-xl border border-[#F2E8E6] bg-white px-2.5 py-3 text-center">
            <Icon className="h-4 w-4 flex-none text-[#E97C6A]" strokeWidth={1.75} />
            <span className="text-[0.66rem] leading-tight text-[#8A7F86]"><strong className="font-semibold text-[#2C2B2C]">{lead}</strong> {rest}</span>
          </div>
        ))}
      </div>

      {/* Secure checkout + payments */}
      <div className="mt-4 flex flex-col items-center gap-2.5 rounded-2xl border border-[#F2E8E6] bg-[#FFFCFA] px-4 py-4">
        <span className="flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.18em] text-[#8A7F86]"><Lock className="h-3 w-3" /> Guaranteed safe checkout</span>
        <PaymentIcons className="justify-center" />
      </div>
      <p className="mt-3 flex items-center gap-2.5 text-sm text-[#8A7F86]">
        <span className="relative flex h-2.5 w-2.5 flex-none">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#5BA86B] opacity-60" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#4E9A5F]" />
        </span>
        <span><strong className="font-medium text-[#2C2B2C]">In stock.</strong> Ships from Gold Coast · Arrives by {arrivalBy()}</span>
      </p>

      {/* See it in action, reel tiles */}
      <div className="mt-7">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#E97C6A]" data-text-slot="pdp_see_in_action">{txt("pdp_see_in_action", "See it in action")}</p>
        <div className="grid grid-cols-4 gap-2.5">
          {VIDS.map((v, i) => (
            <button key={i} onClick={() => setReelIndex(i)} data-testid={`buybox-video-${i}`}
              className="group relative block aspect-[9/16] overflow-hidden rounded-xl border border-[#F2E8E6] bg-[#F7EFEA]">
              <video src={v.src} poster={posterUrl(v.slot)} data-media-slot={v.slot} data-media-type="video" autoPlay muted loop playsInline preload="metadata" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
              <span className="absolute left-1/2 top-1/2 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-[#232222] backdrop-blur-sm transition-transform group-hover:scale-110">
                <Play className="ml-0.5 h-3 w-3 fill-[#232222]" />
              </span>
              <span className="absolute inset-x-1 bottom-1.5 text-center text-[0.56rem] font-medium leading-tight text-white drop-shadow">{v.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Collapsible details */}
      <Accordion type="single" collapsible className="mt-7 w-full space-y-2.5" defaultValue="description">
        <AccordionItem value="description" className="overflow-hidden rounded-2xl border border-[#F2E8E6] bg-[#FFFCFA] px-4 transition-colors data-[state=open]:border-[#FBC4A8]">
          <AccordionTrigger className="py-4 text-left font-display text-base hover:no-underline">
            <span className="flex items-center gap-3"><span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[#FBEEE6] text-[#E97C6A]"><Info className="h-4 w-4" /></span>Product Description</span>
          </AccordionTrigger>
          <AccordionContent className="pb-5">
            <p className="text-sm font-light leading-relaxed text-[#8A7F86]" data-text-slot="pdp_description">
              {txt("pdp_description", "Upgrade your skincare routine with clinically backed LED light therapy that helps reduce the appearance of fine lines, blemishes, pigmentation and dullness, all from the comfort of your own home.")}
            </p>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="specs" className="overflow-hidden rounded-2xl border border-[#F2E8E6] bg-[#FFFCFA] px-4 transition-colors data-[state=open]:border-[#FBC4A8]">
          <AccordionTrigger className="py-4 text-left font-display text-base hover:no-underline">
            <span className="flex items-center gap-3"><span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[#FBEEE6] text-[#E97C6A]"><ListChecks className="h-4 w-4" /></span>Features &amp; Specifications</span>
          </AccordionTrigger>
          <AccordionContent className="pb-5">
            <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">{SPECS.map((x) => <li key={x} className="flex items-center gap-2 text-sm text-[#8A7F86]"><Check className="h-4 w-4 flex-none text-[#FBC4A8]" strokeWidth={2.5} /> {x}</li>)}</ul>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="included" className="overflow-hidden rounded-2xl border border-[#F2E8E6] bg-[#FFFCFA] px-4 transition-colors data-[state=open]:border-[#FBC4A8]">
          <AccordionTrigger className="py-4 text-left font-display text-base hover:no-underline">
            <span className="flex items-center gap-3"><span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[#FBEEE6] text-[#E97C6A]"><Package className="h-4 w-4" /></span>What's in the Box</span>
          </AccordionTrigger>
          <AccordionContent className="pb-5">
            <ul className="space-y-2">{INCLUDED.map((x) => <li key={x} className="flex items-center gap-2 text-sm text-[#8A7F86]"><Check className="h-4 w-4 flex-none text-[#FBC4A8]" strokeWidth={2.5} /> {x}</li>)}</ul>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="how" className="overflow-hidden rounded-2xl border border-[#F2E8E6] bg-[#FFFCFA] px-4 transition-colors data-[state=open]:border-[#FBC4A8]">
          <AccordionTrigger className="py-4 text-left font-display text-base hover:no-underline">
            <span className="flex items-center gap-3"><span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[#FBEEE6] text-[#E97C6A]"><Sparkles className="h-4 w-4" /></span>How to Use It</span>
          </AccordionTrigger>
          <AccordionContent className="pb-5">
            <ol className="space-y-2.5">{STEPS.map((s, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-[#8A7F86]">
                <span className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-[#FBC4A8] text-[0.6rem] font-semibold text-[#2C2B2C]">{i + 1}</span> {s}
              </li>))}
            </ol>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="why" className="overflow-hidden rounded-2xl border border-[#F2E8E6] bg-[#FFFCFA] px-4 transition-colors data-[state=open]:border-[#FBC4A8]">
          <AccordionTrigger className="py-4 text-left font-display text-base hover:no-underline">
            <span className="flex items-center gap-3"><span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[#FBEEE6] text-[#E97C6A]"><Lightbulb className="h-4 w-4" /></span>Why It Works</span>
          </AccordionTrigger>
          <AccordionContent className="pb-5 text-sm font-light leading-relaxed text-[#8A7F86]">
            Clinically-studied wavelengths trigger your skin's own repair response: red (630nm) boosts collagen, blue (415nm) clears acne bacteria, and near-infrared (850nm) accelerates deep cellular renewal, all UV-free, heat-free and gentle enough for daily use.
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="ship" className="overflow-hidden rounded-2xl border border-[#F2E8E6] bg-[#FFFCFA] px-4 transition-colors data-[state=open]:border-[#FBC4A8]">
          <AccordionTrigger className="py-4 text-left font-display text-base hover:no-underline">
            <span className="flex items-center gap-3"><span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[#FBEEE6] text-[#E97C6A]"><Truck className="h-4 w-4" /></span>Shipping &amp; Returns</span>
          </AccordionTrigger>
          <AccordionContent className="pb-5 text-sm font-light leading-relaxed text-[#8A7F86]">
            Free tracked express shipping on every order, dispatched within 24 hours from our Gold Coast studio (2–5 business days within Australia). Protected by a 30-day money-back guarantee and a full 12-month device warranty.
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Stories-style video reel */}
      {reelIndex !== null && (
        <VideoReel clips={reelClips} startIndex={reelIndex} onClose={() => setReelIndex(null)} />
      )}
    </div>
  );
};
